import React, { useState, useEffect } from 'react';
import { 
  GlobeIcon,
  TruckIcon,
  SearchIcon
} from '@heroicons/react/outline';
import api from '../../services/api';

const CourierRateManagement = () => {
  const [loading, setLoading] = useState(false);
  
  // Priority Connections Integration
  const [pcCountries, setPcCountries] = useState([]);
  const [pcRates, setPcRates] = useState([]);
  const [loadingPcRates, setLoadingPcRates] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState('');
  const [rateWeight, setRateWeight] = useState('1.0');
  const [showPcRates, setShowPcRates] = useState(false);

  useEffect(() => {
    fetchPcCountries();
  }, []);

  const fetchPcCountries = async () => {
    try {
      const response = await api.get('/quotes/priority-connections/countries');
      setPcCountries(response.data.countries || []);
    } catch (error) {
      console.error('Error fetching PC countries:', error);
    }
  };

  const fetchPcRates = async () => {
    if (!selectedCountry || !rateWeight) {
      alert('Please select a country and enter weight');
      return;
    }

    try {
      setLoadingPcRates(true);
      const response = await api.get('/quotes/priority-connections/rates', {
        params: {
          country_name: selectedCountry,
          weight: parseFloat(rateWeight),
          parcel_type: 'Parcel'
        }
      });
      
      // Transform raw API data to display format
      const rawRates = response.data.rates || [];
      const formattedRates = rawRates.map(rate => {
        const apiRate = parseFloat(rate.Rate || 0);
        const weight = parseFloat(rateWeight);
        const method = rate.Method || 'Range';
        
        // Calculate base rate based on method
        let baseRate = apiRate;
        if (method === 'Per KG') {
          baseRate = apiRate * weight;
        }
        
        return {
          provider_name: rate.Provider || 'Unknown',
          service_name: `${rate.Provider || 'Unknown'} International`,
          service_type: ['DHL', 'FedEx'].includes(rate.Provider) ? 'express' : 'standard',
          base_rate: baseRate,
          fuel_surcharge: 0,
          insurance_cost: 0,
          total_cost: baseRate,
          estimated_delivery_days: rate.Provider === 'DHL' ? 2 : rate.Provider === 'FedEx' ? 3 : 5,
          zone: rate.Zone || 'N/A',
          method: method,
          api_rate: apiRate
        };
      });
      
      // Sort by total cost (lowest first)
      formattedRates.sort((a, b) => a.total_cost - b.total_cost);
      
      setPcRates(formattedRates);
      setShowPcRates(true);
    } catch (error) {
      console.error('Error fetching PC rates:', error);
      alert('Failed to fetch Priority Connections rates');
    } finally {
      setLoadingPcRates(false);
    }
  };

  const fetchRates = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/carrier-rates');
      const fetchedRates = response.data || [];
      // If no rates from API, use mock rates
      setRates(fetchedRates.length > 0 ? fetchedRates : getMockRates());
    } catch (error) {
      console.error('Error fetching rates:', error);
      setRates(getMockRates());
    } finally {
      setLoading(false);
    }
  };






  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="md:flex md:items-center md:justify-between">
        <div className="flex-1 min-w-0">
          <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
            Courier Rate Management
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            Check and compare international shipping rates from Priority Connections
          </p>
        </div>
      </div>

      {/* Priority Connections Rate Checker */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <GlobeIcon className="w-6 h-6 text-blue-600 mr-3" />
              <div>
                <h3 className="text-lg font-medium text-gray-900">
                  Priority Connections Rate Checker
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  Compare international shipping rates from Priority Connections API
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="px-6 py-4">
          {/* Search Form */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Destination Country
              </label>
              <select
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={selectedCountry}
                onChange={(e) => setSelectedCountry(e.target.value)}
              >
                <option value="">Select Country</option>
                {pcCountries.map((country) => (
                  <option key={country.code} value={country.name}>
                    {country.countryName}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Weight (KG)
              </label>
              <input
                type="number"
                step="0.1"
                min="0.1"
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={rateWeight}
                onChange={(e) => setRateWeight(e.target.value)}
                placeholder="Enter weight"
              />
            </div>

            <div className="flex items-end">
              <button
                onClick={fetchPcRates}
                disabled={loadingPcRates || !selectedCountry || !rateWeight}
                className="w-full inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {loadingPcRates ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Fetching...
                  </>
                ) : (
                  <>
                    <SearchIcon className="w-4 h-4 mr-2" />
                    Get Rates
                  </>
                )}
              </button>
            </div>
          </div>

          {/* PC Rates Display */}
          {showPcRates && (
            <div className="mt-6">
              {pcRates.length > 0 ? (
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-md font-medium text-gray-900">
                      Available Rates for {selectedCountry} ({rateWeight} kg)
                    </h4>
                    <span className="text-sm text-gray-500">
                      {pcRates.length} rate{pcRates.length !== 1 ? 's' : ''} found
                    </span>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {pcRates.map((rate, index) => (
                      <div
                        key={index}
                        className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow bg-gradient-to-br from-white to-blue-50"
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center">
                            <TruckIcon className="w-5 h-5 text-blue-600 mr-2" />
                            <h5 className="font-semibold text-gray-900">
                              {rate.provider_name}
                            </h5>
                          </div>
                          {index === 0 && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                              Best Rate
                            </span>
                          )}
                        </div>

                        <div className="space-y-2">
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600">Total Cost:</span>
                            <span className="text-lg font-bold text-blue-600">
                              ₹{rate.total_cost?.toFixed(2)}
                            </span>
                          </div>

                          <div className="flex justify-between items-center text-sm">
                            <span className="text-gray-600">Rate Method:</span>
                            <span className="text-gray-900 font-medium">{rate.method}</span>
                          </div>

                          {rate.method === 'Per KG' && (
                            <div className="flex justify-between items-center text-sm">
                              <span className="text-gray-600">Per KG Rate:</span>
                              <span className="text-gray-900">₹{rate.api_rate?.toFixed(2)}/kg</span>
                            </div>
                          )}

                          <div className="flex justify-between items-center text-sm">
                            <span className="text-gray-600">Zone:</span>
                            <span className="text-gray-900">{rate.zone}</span>
                          </div>

                          <div className="pt-2 border-t border-gray-200">
                            <div className="flex justify-between items-center text-sm">
                              <span className="text-gray-600">Delivery:</span>
                              <span className="text-gray-900 font-medium">
                                {rate.estimated_delivery_days} days
                              </span>
                            </div>
                          </div>

                          {rate.service_type && (
                            <div className="pt-2">
                              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                rate.service_type === 'express' ? 'bg-purple-100 text-purple-800' :
                                rate.service_type === 'economy' ? 'bg-green-100 text-green-800' :
                                'bg-gray-100 text-gray-800'
                              }`}>
                                {rate.service_type}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Rate Comparison Summary */}
                  <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-start">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-blue-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3 flex-1">
                        <h3 className="text-sm font-medium text-blue-800">Rate Comparison</h3>
                        <div className="mt-2 text-sm text-blue-700">
                          <p>
                            Lowest rate: <span className="font-semibold">₹{Math.min(...pcRates.map(r => r.total_cost || Infinity)).toFixed(2)}</span>
                            {' • '}
                            Highest rate: <span className="font-semibold">₹{Math.max(...pcRates.map(r => r.total_cost || 0)).toFixed(2)}</span>
                            {' • '}
                            Average: <span className="font-semibold">₹{(pcRates.reduce((sum, r) => sum + (r.total_cost || 0), 0) / pcRates.length).toFixed(2)}</span>
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <GlobeIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No rates found</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    No rates available for the selected country and weight.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CourierRateManagement;